import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  TrendingUp,
  Info,
  Crown,
  Key,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';

export interface BeymenCredentials {
  apiKey: string;
  apiPassword: string;
}

interface BeymenConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: BeymenCredentials, connectionId: string) => void;
}

export function BeymenConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: BeymenConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();
  const { checkDuplicateApiKey } = useSubscription();

  const [apiKey, setApiKey] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [storeName, setStoreName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!apiKey.trim() || !apiPassword.trim()) {
      setError(isEnglish ? 'API Key and Password are required' : 'API Anahtari ve Sifre zorunludur');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lutfen once giris yapin');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      // Check for duplicate API key (mukerrer hesap kontrolu)
      const duplicateCheck = await checkDuplicateApiKey(apiKey.trim(), 'beymen');
      if (duplicateCheck.isDuplicate) {
        setStep('form');
        setError(duplicateCheck.message || (isEnglish
          ? 'Duplicate account detected! This API Key is already used in another account. Please check your main account or contact support.'
          : 'Mukerrer hesap tespit edildi! Bu API Anahtari baska bir hesapta kullanilmaktadir. Lutfen ana hesabinizi kontrol edin veya destek ile iletisime gecin.'));
        setIsValidating(false);
        return;
      }

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/saveBeymen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          apiKey: apiKey.trim(),
          apiPassword: apiPassword.trim(),
          storeName: storeName.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        setError(result.error || (isEnglish ? 'Invalid credentials' : 'Gecersiz kimlik bilgileri'));
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: BeymenCredentials = {
        apiKey: apiKey.trim(),
        apiPassword: apiPassword.trim(),
      };

      setStoreName(result.storeName || storeName || 'Beymen Magaza');
      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Baglanti basarisiz. Lutfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setApiKey('');
    setApiPassword('');
    setStoreName('');
    setShowPassword(false);
    setError(null);
    setStep('form');
    onClose();
  };

  if (!isOpen) return null;

  // Beymen brand color - Dark Navy/Black
  const brandColor = '#000000';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header with Beymen branding */}
        <div
          className="px-6 py-5 text-white"
          style={{ background: `linear-gradient(135deg, ${brandColor} 0%, #1a1a1a 100%)` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <Crown className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Beymen</h2>
                <p className="text-white/80 text-sm">
                  {isEnglish ? 'Premium Fashion Marketplace' : 'Premium Moda Pazaryeri'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'success' ? (
            <div className="text-center py-8">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${brandColor}15` }}
              >
                <CheckCircle2 className="w-10 h-10" style={{ color: brandColor }} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isEnglish ? 'Successfully Connected!' : 'Basariyla Baglandi!'}
              </h3>
              <p className="text-gray-600">
                {storeName || 'Beymen Magaza'}
              </p>
            </div>
          ) : step === 'validating' ? (
            <div className="text-center py-8">
              <Loader2
                className="w-12 h-12 mx-auto mb-4 animate-spin"
                style={{ color: brandColor }}
              />
              <p className="text-gray-600">
                {isEnglish ? 'Validating credentials...' : 'Kimlik bilgileri dogrulanıyor...'}
              </p>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" style={{ color: brandColor }} />
                  {isEnglish ? 'What you get' : 'Ne kazanirsiniz'}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <ShoppingBag className="w-4 h-4" style={{ color: brandColor }} />
                    {isEnglish ? 'Product analytics' : 'Urun analitigi'}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Package className="w-4 h-4" style={{ color: brandColor }} />
                    {isEnglish ? 'Order tracking' : 'Siparis takibi'}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <TrendingUp className="w-4 h-4" style={{ color: brandColor }} />
                    {isEnglish ? 'Sales insights' : 'Satis içgoruleri'}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Crown className="w-4 h-4" style={{ color: brandColor }} />
                    {isEnglish ? 'Brand management' : 'Marka yonetimi'}
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Store Name (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {isEnglish ? 'Store Name (Optional)' : 'Magaza Adi (Opsiyonel)'}
                  </label>
                  <input
                    type="text"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    placeholder={isEnglish ? 'My Beymen Store' : 'Beymen Magazam'}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-black/20 focus:border-black transition-all"
                  />
                </div>

                {/* API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      API Key
                    </span>
                  </label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-black/20 focus:border-black transition-all font-mono text-sm"
                  />
                </div>

                {/* API Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      API Password
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={apiPassword}
                      onChange={(e) => setApiPassword(e.target.value)}
                      placeholder="••••••••••••••••"
                      className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-black/20 focus:border-black transition-all font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isValidating || !apiKey.trim() || !apiPassword.trim()}
                  className={cn(
                    'w-full py-3.5 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2',
                    isValidating || !apiKey.trim() || !apiPassword.trim()
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'hover:opacity-90 shadow-lg hover:shadow-xl'
                  )}
                  style={{
                    backgroundColor: isValidating || !apiKey.trim() || !apiPassword.trim() ? undefined : brandColor,
                  }}
                >
                  {isValidating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Baglaniyor...'}
                    </>
                  ) : (
                    <>
                      <Crown className="w-5 h-5" />
                      {isEnglish ? 'Connect Beymen' : 'Beymen Bagla'}
                    </>
                  )}
                </button>
              </form>

              {/* Help Link */}
              <div className="mt-4 text-center">
                <a
                  href="https://partner-docs.beymen.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {isEnglish ? 'API Documentation' : 'API Dokumantasyonu'}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
